/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * FIXHEN first-party analytics tracker.
 *
 * Design goals:
 *  - Zero impact on LCP: installed lazily, after the error tracker, from an
 *    idle callback. Never blocks render or navigation.
 *  - Zero third-party requests, zero cookies → no consent banner needed.
 *  - Batched delivery via `navigator.sendBeacon` (or keepalive fetch) so a
 *    navigation / tab close never loses events and never delays the unload.
 *  - Respects Do Not Track and the existing opt-out localStorage key.
 */
import { scrubUrl, scrubValue } from "@/lib/client-errors/scrub";
import { installDiagnostics, describeField } from "./diagnostics";

const ENDPOINT = "/api/public/analytics";
const SESSION_KEY = "fx_a_session_v1";
const SESSION_TS_KEY = "fx_a_session_ts_v1";
const VISITOR_KEY = "fx_a_visitor_v1";
const OPT_OUT_KEY = "opt_out_analytics";
const ERR_OPT_OUT_KEY = "opt_out_error_tracking";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min inactivity → new session
const FLUSH_MS = 5000;
const MAX_BATCH = 40;

export type AnalyticsEventType =
  | "page_view"
  | "click"
  | "scroll_depth"
  | "search"
  | "filter"
  | "product_view"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "purchase"
  | "signup"
  | "login"
  | "logout"
  | "error"
  | "vitals"
  | "custom";

interface QueuedEvent {
  ts: string;
  t_ms: number;
  type: AnalyticsEventType;
  name?: string;
  path?: string;
  value?: number;
  props?: Record<string, unknown>;
}

let installed = false;
let queue: QueuedEvent[] = [];
/**
 * Commerce events (add_to_cart, purchase, …) can fire before the tracker is
 * installed — installation is deliberately deferred ~2.6s so it never competes
 * with LCP, while an express "Buy with Apple Pay" can complete far sooner.
 * Buffer those events instead of dropping them, otherwise the funnel shows 0
 * carts / 0 purchases for the fastest (most valuable) sessions.
 */
let pending: QueuedEvent[] = [];
const MAX_PENDING = 30;
let flushTimer: number | undefined;
let t0 = 0;
let country: string | null = null;
let lastPath = "";
let pageEnteredAt = 0;
let maxScroll = 0;
// Visible-only dwell: a background tab must not count as "reading the page".
let pageVisibleMs = 0;
let visibleSince: number | null = null;
function takeActiveSecs(): number {
  const now = performance.now();
  const ms = pageVisibleMs + (visibleSince != null ? now - visibleSince : 0);
  pageVisibleMs = 0;
  visibleSince = typeof document !== "undefined" && document.visibilityState === "visible" ? now : null;
  return Math.round(ms / 1000);
}
let authToken: string | null = null;

/**
 * Attach the signed-in user's access token so the ingest RPC can resolve
 * auth.uid() and link the session to a customer. Pass null on sign-out.
 * Never stored — kept in memory for the tab's lifetime only.
 */
export function setAnalyticsToken(token: string | null) {
  authToken = token;
}


const INTERNAL_KEY = "fx_a_internal_v1";

/**
 * Internal / non-customer traffic: admin panel, Lovable preview iframes
 * (`?__lovable_sha=…`), local dev. These are us, not real visitors — never
 * report them. Once a session touches an internal surface it stays excluded.
 */
function internalNow(): boolean {
  try {
    const p = window.location.pathname || "";
    if (/^\/(admin|lovable)(\/|$)/i.test(p)) return true;
    if (/__lovable/i.test(window.location.search || "")) return true;
    const h = window.location.hostname || "";
    if (h === "localhost" || h === "127.0.0.1" || h.endsWith(".lovableproject.com")) return true;
    if (h.startsWith("id-preview--")) return true;
    return false;
  } catch {
    return false;
  }
}

function internalSession(): boolean {
  try {
    if (sessionStorage.getItem(INTERNAL_KEY) === "1") return true;
    if (internalNow()) {
      sessionStorage.setItem(INTERNAL_KEY, "1");
      return true;
    }
    return false;
  } catch {
    return internalNow();
  }
}

function optedOut(): boolean {
  try {
    if (typeof navigator !== "undefined" && (navigator as any).doNotTrack === "1") return true;
    return (
      localStorage.getItem(OPT_OUT_KEY) === "1" || localStorage.getItem(ERR_OPT_OUT_KEY) === "1"
    );
  } catch {
    return false;
  }
}

function rid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function getVisitorId(): string {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = rid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

function getSessionId(): string {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(SESSION_TS_KEY) || localStorage.getItem(SESSION_TS_KEY) || 0);
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid || (last && now - last > SESSION_IDLE_MS)) {
      sid = rid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    try {
      localStorage.setItem(SESSION_TS_KEY, String(now));
    } catch {
      /* ignore */
    }
    return sid;
  } catch {
    return "no-storage-" + rid().slice(0, 8);
  }
}

function utm(): Record<string, string | undefined> {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") ?? p.get("ref") ?? undefined,
      utm_medium: p.get("utm_medium") ?? undefined,
      utm_campaign: p.get("utm_campaign") ?? undefined,
      utm_term: p.get("utm_term") ?? undefined,
      utm_content: p.get("utm_content") ?? undefined,
    };
  } catch {
    return {};
  }
}

function meta() {
  let referrerHost: string | undefined;
  try {
    if (document.referrer && !document.referrer.startsWith(window.location.origin)) {
      referrerHost = new URL(document.referrer).host;
    }
  } catch {
    /* ignore */
  }
  return {
    entry_path: scrubUrl(window.location.pathname + window.location.search),
    referrer: document.referrer ? scrubUrl(document.referrer) : undefined,
    referrer_host: referrerHost,
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    country: country ?? undefined,
    ...utm(),
  };
}

function send(events: QueuedEvent[], useBeacon: boolean) {
  if (!events.length) return;
  const payload = JSON.stringify({
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    meta: meta(),
    events,
  });
  try {
    // sendBeacon can't carry an Authorization header, so signed-in traffic
    // uses keepalive fetch instead (still fires during unload).
    if (useBeacon && !authToken && typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      if (ok) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: authToken
        ? { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }
        : { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});

  } catch {
    /* never let analytics throw */
  }
}

function flush(useBeacon = false) {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_BATCH);
  send(batch, useBeacon);
  if (flushTimer) {
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    const run = () => flush(false);
    if (typeof (window as any).requestIdleCallback === "function") {
      (window as any).requestIdleCallback(run, { timeout: 2000 });
    } else {
      run();
    }
  }, FLUSH_MS);
}

/** Public API — safe to call before install (no-op until installed). */
export function track(
  type: AnalyticsEventType,
  opts: { name?: string; path?: string; value?: number; props?: Record<string, unknown> } = {},
) {
  if (typeof window === "undefined" || optedOut()) return;
  if (internalSession()) return;
  const event: QueuedEvent = {
    ts: new Date().toISOString(),
    t_ms: Math.round(performance.now() - t0),
    type,
    name: opts.name ? String(opts.name).slice(0, 300) : undefined,
    path: opts.path ?? scrubUrl(window.location.pathname + window.location.search),
    value: typeof opts.value === "number" && Number.isFinite(opts.value) ? opts.value : undefined,
    props: opts.props ? (scrubValue(opts.props) as Record<string, unknown>) : undefined,
  };
  if (!installed) {
    // Buffered until installAnalytics() drains it (see `pending` above).
    if (pending.length < MAX_PENDING) pending.push(event);
    return;
  }
  queue.push(event);
  if (queue.length >= MAX_BATCH) flush(false);
  else scheduleFlush();
}

// ── Commerce helpers ────────────────────────────────────────────────────────
// These map 1:1 onto the funnel steps the Traffic panel renders, so any new
// call site automatically shows up there.

export function trackProductView(p: { sku?: string | null; slug?: string | null; title?: string | null; priceEur?: number | null }) {
  track("product_view", {
    name: p.title ?? p.sku ?? p.slug ?? undefined,
    value: typeof p.priceEur === "number" ? p.priceEur : undefined,
    props: { sku: p.sku ?? undefined, slug: p.slug ?? undefined },
  });
}

export function trackAddToCart(p: { sku?: string | null; title?: string | null; qty?: number; priceEur?: number | null }) {
  track("add_to_cart", {
    name: p.title ?? p.sku ?? undefined,
    value: typeof p.priceEur === "number" ? p.priceEur * (p.qty ?? 1) : undefined,
    props: { sku: p.sku ?? undefined, qty: p.qty ?? 1 },
  });
}

export function trackRemoveFromCart(p: { sku?: string | null; title?: string | null; qty?: number }) {
  track("remove_from_cart", { name: p.title ?? p.sku ?? undefined, props: { sku: p.sku ?? undefined, qty: p.qty ?? 1 } });
}

export function trackViewCart(p: { items: number; subtotalEur: number }) {
  track("view_cart", { value: p.subtotalEur, props: { items: p.items } });
}

export function trackBeginCheckout(p: { items: number; subtotalEur: number; method: "standard" | "express" }) {
  track("begin_checkout", { name: p.method, value: p.subtotalEur, props: { items: p.items, method: p.method } });
}

const PURCHASE_SEEN_KEY = "fx_a_purchased_v1";

/**
 * Fires once per order, ever (guarded in localStorage) — the success page is
 * pollable and refreshable, and double-counting would inflate revenue.
 */
export function trackPurchase(p: { orderId: string; orderNumber?: string | null; totalEur: number; items?: number; method?: string }) {
  if (typeof window === "undefined" || !p.orderId) return;
  try {
    const raw = window.localStorage.getItem(PURCHASE_SEEN_KEY);
    const seen: string[] = raw ? JSON.parse(raw) : [];
    if (seen.includes(p.orderId)) return;
    window.localStorage.setItem(PURCHASE_SEEN_KEY, JSON.stringify([...seen, p.orderId].slice(-50)));
  } catch {
    /* storage disabled — still report, a duplicate beats a missing sale */
  }
  track("purchase", {
    name: p.orderNumber ? `#${p.orderNumber}` : p.orderId,
    value: p.totalEur,
    props: { order_id: p.orderId, order_number: p.orderNumber ?? undefined, items: p.items, method: p.method ?? "standard" },
  });
  // A purchase is the one event we never want to lose to a tab close.
  flush(false);
}

/** A step inside the checkout flow (country picked, shipping shown, payment UI ready…). */
export function trackCheckoutStep(step: string, props?: Record<string, unknown>) {
  track("custom", { name: `checkout_${step}`, props });
}

/** Anything that stops a buyer from paying: declined card, failed session, validation. */
export function trackCheckoutError(message: string, props?: Record<string, unknown>) {
  track("custom", { name: "checkout_error", value: 1, props: { message: String(message).slice(0, 240), ...props } });
}

function emitPageView(path: string) {
  if (path === lastPath) return;
  // close out previous page with its dwell time + scroll depth
  if (lastPath && pageEnteredAt) {
    track("custom", {
      name: "page_exit",
      path: lastPath,
      value: takeActiveSecs(),
      props: { scroll_pct: maxScroll, wall_secs: Math.round((performance.now() - pageEnteredAt) / 1000) },
    });
  }
  lastPath = path;
  pageEnteredAt = performance.now();
  pageVisibleMs = 0;
  visibleSince = document.visibilityState === "visible" ? pageEnteredAt : null;
  maxScroll = 0;
  track("page_view", { path, name: document.title?.slice(0, 200) });
}

export function installAnalytics() {
  if (typeof window === "undefined" || installed) return;
  if (optedOut() || internalSession()) return;
  installed = true;
  t0 = performance.now();
  if (pending.length) {
    queue.push(...pending);
    pending = [];
  }
  getSessionId();
  getVisitorId();

  // country hint (cached, non-blocking)
  try {
    const cached = sessionStorage.getItem("fx_a_country");
    if (cached) country = cached;
    else
      void fetch("/api/public/geo")
        .then((r) => r.json())
        .then((d) => {
          if (d?.country) {
            country = d.country;
            try {
              sessionStorage.setItem("fx_a_country", d.country);
            } catch {
              /* ignore */
            }
          }
        })
        .catch(() => {});
  } catch {
    /* ignore */
  }

  emitPageView(scrubUrl(window.location.pathname + window.location.search));

  // SPA navigations — poll cheaply on rAF-throttled interval + popstate
  let lastHref = window.location.href;
  const checkNav = () => {
    if (window.location.href !== lastHref) {
      lastHref = window.location.href;
      emitPageView(scrubUrl(window.location.pathname + window.location.search));
    }
  };
  window.addEventListener("popstate", checkNav);
  window.setInterval(checkNav, 700);

  // clicks on interactive elements
  window.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest(
        "button, a, [role=button], [data-track]",
      ) as HTMLElement | null;
      if (!el) return;
      const name =
        el.getAttribute("data-track") ||
        el.getAttribute("aria-label") ||
        (el.textContent ?? "").trim().replace(/\s+/g, " ").slice(0, 80);
      if (!name) return;
      track("click", {
        name,
        props: {
          tag: el.tagName.toLowerCase(),
          href:
            el instanceof HTMLAnchorElement ? scrubUrl(el.getAttribute("href") ?? "") : undefined,
        },
      });
    },
    { capture: true, passive: true },
  );

  // scroll depth milestones
  let scrollTick = false;
  window.addEventListener(
    "scroll",
    () => {
      if (scrollTick) return;
      scrollTick = true;
      requestAnimationFrame(() => {
        scrollTick = false;
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        if (total <= 0) return;
        const pct = Math.min(100, Math.round(((window.scrollY || 0) / total) * 100));
        for (const m of [25, 50, 75, 100]) {
          if (pct >= m && maxScroll < m) {
            maxScroll = m;
            track("scroll_depth", { value: m });
          }
        }
      });
    },
    { passive: true },
  );

  // ── Frustration signals ───────────────────────────────────────────────────
  // Rage clicks: 3+ clicks in the same small area within 900ms. Strong signal
  // that something looks clickable but does nothing, or is too slow.
  let clickBurst: Array<{ x: number; y: number; t: number }> = [];
  let lastRageAt = 0;
  window.addEventListener(
    "click",
    (e) => {
      const now = performance.now();
      clickBurst = clickBurst.filter((c) => now - c.t < 900);
      clickBurst.push({ x: e.clientX, y: e.clientY, t: now });
      if (clickBurst.length < 3 || now - lastRageAt < 3000) return;
      const first = clickBurst[0];
      const near = clickBurst.every(
        (c) => Math.abs(c.x - first.x) < 40 && Math.abs(c.y - first.y) < 40,
      );
      if (!near) return;
      lastRageAt = now;
      clickBurst = [];
      const el = (e.target as HTMLElement | null)?.closest(
        "button, a, [role=button], input, label, div",
      ) as HTMLElement | null;
      track("custom", {
        name: "rage_click",
        value: 3,
        props: {
          label: (el?.getAttribute("aria-label") || el?.textContent || "").trim().slice(0, 80),
          tag: el?.tagName.toLowerCase(),
        },
      });
    },
    { capture: true, passive: true },
  );

  // Form fields on money pages: which one they touched, whether they left it empty.
  const FORM_PATHS = /^\/(checkout|cart|auth|account)/i;
  const fieldName = (el: HTMLElement) => describeField(el);
  let fieldFocusedAt = 0;
  window.addEventListener(
    "focusin",
    (e) => {
      if (!FORM_PATHS.test(window.location.pathname)) return;
      const el = e.target as HTMLElement | null;
      if (!el || !/^(input|select|textarea)$/i.test(el.tagName)) return;
      if ((el as HTMLInputElement).type === "password") return;
      fieldFocusedAt = performance.now();
      track("custom", { name: "field_focus", props: { field: fieldName(el) } });
    },
    { capture: true, passive: true },
  );
  window.addEventListener(
    "focusout",
    (e) => {
      if (!FORM_PATHS.test(window.location.pathname)) return;
      const el = e.target as HTMLElement | null;
      if (!el || !/^(input|select|textarea)$/i.test(el.tagName)) return;
      if ((el as HTMLInputElement).type === "password") return;
      const filled = !!(el as HTMLInputElement).value?.trim();
      const secs = fieldFocusedAt ? Math.round((performance.now() - fieldFocusedAt) / 100) / 10 : 0;
      track("custom", {
        name: filled ? "field_filled" : "field_left_empty",
        value: secs,
        props: { field: fieldName(el) },
      });
    },
    { capture: true, passive: true },
  );

  // Exit intent on the money pages (cursor thrown to the top of the window).
  let exitIntentSent = false;
  document.addEventListener("mouseout", (e) => {
    if (exitIntentSent || (e as MouseEvent).clientY > 4) return;
    if (!/^\/(checkout|cart)/i.test(window.location.pathname)) return;
    exitIntentSent = true;
    track("custom", { name: "exit_intent", path: lastPath });
  });

  // Tab switching away from checkout — comparison shopping / hesitation.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    if (!/^\/(checkout|cart)/i.test(window.location.pathname)) return;
    track("custom", { name: "left_tab", path: lastPath });
  });

  // flush on hide / unload
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (internalSession()) return;
      if (lastPath && pageEnteredAt) {
        queue.push({
          ts: new Date().toISOString(),
          t_ms: Math.round(performance.now() - t0),
          type: "custom",
          name: "page_exit",
          path: lastPath,
          value: takeActiveSecs(),
          props: { scroll_pct: maxScroll, visible_only: true },
        });
        pageEnteredAt = performance.now();
      }
      flush(true);
    } else if (visibleSince == null) {
      visibleSince = performance.now();
    }
  });
  window.addEventListener("pagehide", () => flush(true));

  installDiagnostics({ track, endpoint: ENDPOINT, formPaths: FORM_PATHS });
}

/** Turn tracking off for this browser (GDPR / user preference). */
export function setAnalyticsOptOut(off: boolean) {
  try {
    if (off) localStorage.setItem(OPT_OUT_KEY, "1");
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* ignore */
  }
}
